'use client';

import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { SessionManagerState } from './types';
import { createActivityDetector } from './modules/activity-detector';
import { createRefreshScheduler } from './modules/refresh-scheduler';
import { createIdleTimeoutManager } from './modules/idle-timeout-manager';
import { executeLogout } from './modules/logout-handler';
import { SessionWarningDialog } from './ui/SessionWarningDialog';
import { resolveAuthContext } from '@/utils/auth-context';

const SessionManagerContext = createContext<SessionManagerState | null>(null);

export function useSessionManager() {
    const context = useContext(SessionManagerContext);
    if (!context) {
        throw new Error('useSessionManager must be used within a SessionManagerProvider');
    }
    return context;
}

function SessionManagerProviderInner({ children }: { children: ReactNode }) {
    const { data: session, update: updateSession } = useSession();
    const policy = (session as any)?.policy;

    const [state, setState] = useState<SessionManagerState>({
        isIdle: false,
        showWarning: false,
        warningCountdown: 0,
        lastActivity: Date.now(),
        isRefreshing: false,
    });

    const stateRef = useRef(state);
    stateRef.current = state;

    // Ref to always access latest session inside closures created at mount time
    const sessionRef = useRef(session);
    sessionRef.current = session;

    // References to our modules
    const modulesRef = useRef<{
        activityDetector?: ReturnType<typeof createActivityDetector>;
        refreshScheduler?: ReturnType<typeof createRefreshScheduler>;
        idleManager?: ReturnType<typeof createIdleTimeoutManager>;
    }>({});

    // 1. Initialize modules when provider mounts
    useEffect(() => {
        if (typeof window === 'undefined') return;

        // Initialize Activity Detector
        const activityDetector = createActivityDetector(2000); // 2s throttle
        activityDetector.start();
        modulesRef.current.activityDetector = activityDetector;

        // Initialize Refresh Scheduler
        const refreshScheduler = createRefreshScheduler({
            getTokenExpiry: () => {
                // Use ref to always get the latest session value (avoid stale closure)
                const extendedSession = sessionRef.current as any;
                if (extendedSession?.accessTokenExpires) {
                    // accessTokenExpires is already a ms timestamp (Date.now() + ttl*1000)
                    return extendedSession.accessTokenExpires;
                }
                return undefined;
            },
            isUserActive: (thresholdMs) => activityDetector.isActive(thresholdMs),
            refreshFn: async () => {
                setState(prev => ({ ...prev, isRefreshing: true }));
                try {
                    // updateSession() triggers the jwt callback in next-auth which will refresh if needed
                    await updateSession();
                    return true;
                } catch (e) {
                    return false;
                } finally {
                    setState(prev => ({ ...prev, isRefreshing: false }));
                }
            },
            thresholdMs: 120_000, // 2 minutes before expiry
        });
        modulesRef.current.refreshScheduler = refreshScheduler;

        // Initialize Idle Manager
        const idleManager = createIdleTimeoutManager({
            getLastActivity: () => activityDetector.getLastActivity(),
            idleTimeoutMs: 3600_000, // Default 1 hr, will be updated when policy loads
            warningBeforeMs: 300_000, // Default 5 mins, will be updated when policy loads
            intervalMs: 1000, // Check every 1 second for real-time countdown UX
            onWarning: (countdown) => {
                modulesRef.current.activityDetector?.pause();
                setState(prev => ({ ...prev, showWarning: true, warningCountdown: countdown }));
            },
            onIdle: () => {
                setState(prev => ({ ...prev, isIdle: true, showWarning: false }));
                executeLogout('idle');
            },
            onActive: () => {
                modulesRef.current.activityDetector?.resume();
                setState(prev => ({ ...prev, showWarning: false }));
            }
        });
        modulesRef.current.idleManager = idleManager;

        return () => {
            activityDetector.stop();
            refreshScheduler.stop();
            idleManager.stop();
        };
    }, []); // Run once on mount

    // 2. React to Session Policy changes
    useEffect(() => {
        if (!policy) return;

        // We only care about policy if user is logged in
        const idleManager = modulesRef.current.idleManager;
        const refreshScheduler = modulesRef.current.refreshScheduler;

        if (idleManager) {
            // DB stores idle_timeout and warning_before_expire in SECONDS, convert to ms
            const idleTimeoutMs = (policy.idle_timeout || 1800) * 1000;
            const warningBeforeMs = (policy.warning_before_expire || 300) * 1000;
            idleManager.updateConfig(idleTimeoutMs, warningBeforeMs);
            idleManager.start(); // Ensure it's running with new config
        }

        if (refreshScheduler) {
            if (Number(policy.auto_refresh) === 1 || policy.auto_refresh === true) {
                refreshScheduler.start();
            } else {
                refreshScheduler.stop();
            }
        }
    }, [policy]);

    const handleContinue = () => {
        modulesRef.current.activityDetector?.resume();
        setState(prev => ({ ...prev, showWarning: false }));
        modulesRef.current.activityDetector?.resetActivity();
        // Proactively try to refresh
        if (policy?.auto_refresh === 1) {
             modulesRef.current.refreshScheduler?.forceRefresh();
        }
    };

    // Handle logout button
    const handleLogout = () => {
        executeLogout('manual');
    };

    return (
        <SessionManagerContext.Provider value={state}>
            {children}
            <SessionWarningDialog
                open={state.showWarning}
                countdown={state.warningCountdown}
                inactiveDuration={(policy?.idle_timeout || 1800) - state.warningCountdown}
                onContinue={handleContinue}
                onLogout={handleLogout}
            />
        </SessionManagerContext.Provider>
    );
}

export function SessionManagerProvider({ children }: { children: ReactNode }) {
    const pathname = usePathname() || '';
    
    // Heritage và Public pages không cần session management của Admin
    const context = resolveAuthContext(pathname);
    if (context === 'HERITAGE' || context === 'PUBLIC') {
        return <>{children}</>;
    }
    
    return <SessionManagerProviderInner>{children}</SessionManagerProviderInner>;
}